
import { useState } from "react"
import { Link } from "react-router-dom"
import type { Project } from "../hooks/types"

interface ProjectsTableProps {
  projects: Project[]
  isLoading: boolean
  onEdit: (project: Project) => void
  onDelete: (project: Project) => void
}

export default function ProjectsTable({ projects, isLoading, onEdit, onDelete }: ProjectsTableProps) {
  const [sortField, setSortField] = useState<keyof Project>("projectCode")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")

  const handleSort = (field: keyof Project) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const sortedProjects = [...projects].sort((a: Project, b: Project) => {
    let aValue = a[sortField] as string | number
    let bValue = b[sortField] as string | number

    // Handle nested properties
    if (sortField === "clientId") {
      aValue = a.clientId.name
      bValue = b.clientId.name
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1
    return 0
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Planning":
        return "bg-blue-100 text-blue-800"
      case "Bidding":
        return "bg-purple-100 text-purple-800"
      case "Awarded":
        return "bg-green-100 text-green-800"
      case "Fabrication":
        return "bg-amber-100 text-amber-800"
      case "Erection":
        return "bg-orange-100 text-orange-800"
      case "Completed":
        return "bg-gray-100 text-gray-800"
      case "OnHold":
        return "bg-yellow-100 text-yellow-800"
      case "Cancelled":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  if (isLoading) {
    return (
      <div className="w-full h-64 flex items-center justify-center">
        <div className="rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  if (projects.length === 0) {
    return (
      <div className="w-full p-8 text-center border rounded-lg bg-white">
        <h3 className="text-lg font-medium text-gray-900 mb-2">No projects found</h3>
        <p className="text-gray-500 mb-4">Get started by creating your first project.</p>
        <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Create Project</button>
      </div>
    )
  }

  return (
    <div className="rounded-md border bg-white overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => handleSort("projectCode")}
            >
              Code {sortField === "projectCode" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => handleSort("title")}
            >
              Title {sortField === "title" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => handleSort("status")}
            >
              Status {sortField === "status" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => handleSort("clientId")}
            >
              Client {sortField === "clientId" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              onClick={() => handleSort("totalBudgetHours")}
            >
              Budget Hours {sortField === "totalBudgetHours" && (sortDirection === "asc" ? "↑" : "↓")}
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedProjects.map((project) => (
            <tr key={project._id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{project.projectCode}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <Link to={`/myteconstruction/dashboard/projects/${project._id}`} className="text-blue-600 hover:underline">
                  {project.title}
                </Link>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(project.status)}`}
                >
                  {project.status}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{project.clientId.name}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {project.totalBudgetHours.toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <button onClick={() => onEdit(project)} className="text-blue-600 hover:text-blue-900 mr-4">
                  Edit
                </button>
                <button onClick={() => onDelete(project)} className="text-red-600 hover:text-red-900">
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
